import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { NewPersonLink } from '../components/people/new-person-link';
import {
  SUBJECT_LIST_LEVELS,
  SUBJECT_LIST_SORTS,
  SUBJECT_RESEARCH_STATUSES,
  SUBJECT_VOCABULARY_BY_VERTICAL,
  hasStatusChoice,
  type KnowledgeSearchResponse,
  type SubjectListEntry,
  type SubjectListLevel,
  type SubjectListSort,
  type SubjectResearchStatus,
  type SubjectStatus,
} from '@ni/shared';
import { useOrg } from '../api/app-context';
import { AccountChoice } from './accounts';
import { useAlsoKnown, useCreateSubject, useImportSession, usePeople } from '../api/hooks';
import { loadErrorLine } from '../api/errors';
import { PeopleTable, type PromoteState } from '../components/people/people-table';
import { ErrorLine } from '../components/ui/error-line';
import { WorkingButton } from '../components/ui/working-button';
import { SEARCH_DEBOUNCE_MS, useSettled } from '../lib/debounce';
import { alsoKnownCount } from '../lib/knowledge';
import { flattenPages, promoteBody } from '../lib/people-query';
import { orgHasProtectionAccounts } from '../lib/vertical';
import {
  LOADING_MORE,
  LOAD_MORE,
  PEOPLE_EMPTY_BY_LEVEL,
  PEOPLE_NONE_MATCH,
  PEOPLE_SEARCH_PLACEHOLDER,
  PEOPLE_IMPORT_FILTER_CLEAR,
  PEOPLE_SORT_LABELS,
  alsoKnownLine,
  peopleImportFilterLine,
  subjectStatusLabel,
} from '../lib/copy';
import { Loading } from '../components/ui/loading';
import { EmptyLine } from '../components/ui/empty-line';
import { ListSearch } from '../components/ui/list-search';
import { Pick } from '../components/ui/pick';
import './people.css';

/** Filters are part of the query key, so each selection starts at its own first page. */
export function People() {
  const org = useOrg();
  const navigate = useNavigate();

  /* **The one control that lives on the address** (import note §2.6). The visible
     controls are state, because a reader arrives at them here; this one is arrived
     at from somewhere else — the link at the end of a commit, the record's own
     subline — so it has to survive being pasted, and it has to be clearable back
     to the ordinary list. */
  const [params, setParams] = useSearchParams();
  const importId = params.get('import');
  const importedIn = useImportSession(importId);

  /* **The account chip, on the address for the reason the import filter is**
     (security fold, 2026-09-22): it is arrived at from the account page and
     from a record's own subline, so it has to survive being pasted. Read only
     in a security org — every other vertical refuses the parameter, and a
     stray `?account_id=` in a club's address would turn a whole list into a
     400 nobody asked for. */
  const security = orgHasProtectionAccounts(org);
  const accountId = security ? params.get('account_id') : null;

  const [typed, setTyped] = useState('');
  const [q, search] = useSettled('', SEARCH_DEBOUNCE_MS);
  const [statuses, setStatuses] = useState<readonly string[]>([]);
  /* An import writes known people as well as tracked ones (§1.3), so a link that
     asks for one carries `level` too and this reads it — the default would show
     half of what the import wrote and read as a failure. An unknown word falls
     back rather than being sent: it is a URL, and a URL outlives the screen. */
  const [level, setLevel] = useState<SubjectListLevel>(() => {
    const asked = params.get('level');
    return SUBJECT_LIST_LEVELS.includes(asked as SubjectListLevel)
      ? (asked as SubjectListLevel)
      : 'tracked';
  });
  const [researchStatus, setResearchStatus] = useState<SubjectResearchStatus | ''>('');
  const [sort, setSort] = useState<SubjectListSort>('updated');

  const people = usePeople({ q, statuses, level, sort, researchStatus, importId, accountId });

  /* The adopt door's two pieces of state: which row is asking, and which row is
     writing. Both are entity ids, because a known person has nothing else. */
  const [openFor, setOpenFor] = useState<string | null>(null);
  const [busyFor, setBusyFor] = useState<string | null>(null);
  const create = useCreateSubject();

  const promote: PromoteState = {
    vertical: org.vertical,
    openFor,
    busyFor,
    error: create.isError ? create.error : null,
    onOpen: (entityId) => {
      if (create.isError) create.reset();
      setOpenFor(entityId);
    },
    onChoose: (entry: SubjectListEntry, status: SubjectStatus) => {
      if (create.isPending) return;
      setBusyFor(entry.entity_id);
      create.mutate(promoteBody(entry, status), {
        onSuccess: (record) => {
          setOpenFor(null);
          navigate(`/people/${record.subject.id}`);
        },
        onSettled: () => setBusyFor(null),
      });
    },
  };

  /* The same word, put to the knowledge base and narrowed to people — off
     unless this is the ordinary tracked list, since a list already showing
     known people has nothing to add a footnote about. */
  const alsoKnown = useAlsoKnown(q, level === 'tracked');

  const entries = flattenPages(people.data?.pages);
  const searching = q.trim().length > 0 || statuses.length > 0 || researchStatus !== '';

  return (
    <div className="column people">
      <div className="head">
        <div className="titles">
          <div className="title">People</div>
        </div>
        {/* A security org's list is one account or all of them: a scope over the
            page rather than one more filter, so it sits in the head and the
            toolbar keeps the four controls every vertical has. */}
        {security ? (
          <AccountChoice
            value={accountId ?? ''}
            empty="All accounts"
            variant="pick"
            onChange={(id) => {
              const next = new URLSearchParams(params);
              if (id) next.set('account_id', id);
              else next.delete('account_id');
              setParams(next);
            }}
          />
        ) : null}
        <NewPersonLink />
      </div>

      {importId ? (
        <div className="import-filter">
          <span>{peopleImportFilterLine(importedIn.data?.description ?? '…')}</span>
          <button
            type="button"
            className="quiet-action"
            onClick={() => {
              setParams({});
              setLevel('tracked');
            }}
          >
            {PEOPLE_IMPORT_FILTER_CLEAR}
          </button>
        </div>
      ) : null}

      <PeopleControls
        vertical={org.vertical}
        typed={typed}
        statuses={statuses}
        level={level}
        sort={sort}
        researchStatus={researchStatus}
        onResearchStatus={setResearchStatus}
        onTyped={(next) => {
          setTyped(next);
          search(next);
        }}
        onStatus={(word) => setStatuses(word ? [word] : [])}
        onSort={setSort}
      />

      <div className="people-list">
        {/* **Rows first, and a failure reported where it happened.** The order
            of these branches used to be `isError` before `entries.length`,
            which made one refused Load more — a 500, or the stale-cursor 400
            every reader holding a cursor gets the moment a deploy bumps
            `CURSOR_VERSION` — replace four pages of people with a single line.
            The pages that *did* load are still true, and throwing them away is
            both a loss of the reader's place and a wildly overstated account of
            what went wrong. So `isError` decides only what is said in the foot,
            beside the Load more that failed; it can empty the screen only when
            the screen was empty anyway, which is the first page failing. */}
        {people.isPending ? (
          <EmptyLine>
            <Loading />
          </EmptyLine>
        ) : entries.length === 0 ? (
          <EmptyLine>
            {people.isError
              ? loadErrorLine(people.error)
              : searching
                ? PEOPLE_NONE_MATCH
                : org.is_demo
                  ? 'No stored people in this view.'
                  : PEOPLE_EMPTY_BY_LEVEL[level]}
          </EmptyLine>
        ) : (
          <>
            <PeopleTable entries={entries} promote={promote} account={security} />
            {people.hasNextPage || people.isError ? (
              <div className="list-foot">
                {people.hasNextPage ? (
                  <WorkingButton
                    label={LOAD_MORE}
                    workingLabel={LOADING_MORE}
                    working={people.isFetchingNextPage}
                    variant="act"
                    tiny
                    onClick={() => void people.fetchNextPage()}
                  />
                ) : null}
                {/* The same quiet source line every failed write in the app
                    gets, next to the button that is the retry. */}
                {people.isError ? <ErrorLine error={people.error} load /> : null}
              </div>
            ) : null}
          </>
        )}

        {/* Under the rows rather than over them: it is a footnote about the
            people this list is not for, and it has to be there in the case that
            matters most — a search that matches nobody tracked at all. Not
            while the list itself is still loading, though: the two reads answer
            at their own speeds, and "Loading." over "Also 12 known people
            match" is a screen telling a reader about the people it is not
            showing before it has said anything about the ones it is. */}
        {people.isPending ? null : <AlsoKnown q={q} answer={alsoKnown.data} />}
      </div>
    </div>
  );
}

/**
 * **"Also 12 known people match"** (knowledge-base note §1.6) — the one line
 * People keeps about the people it stopped listing.
 *
 * It is a link and not a note, because the answer to "and who are they" is a
 * page: the same search, on the knowledge base, narrowed to people. It says
 * nothing at all when nothing matches, which is the ordinary case; a line that
 * read "Also 0 known people match" under every search would be noise on every
 * screen to save a reader one disappointment on a few.
 */
export function AlsoKnown({
  q,
  answer,
}: {
  q: string;
  /** The knowledge search, or undefined while it is off or in flight. */
  answer: KnowledgeSearchResponse | undefined;
}) {
  const count = alsoKnownCount(answer);
  if (count === 0) return null;

  return (
    <div className="src also-known">
      <Link className="link" to={`/knowledge?q=${encodeURIComponent(q.trim())}&kind=person`}>
        {alsoKnownLine(count, answer?.capped ?? false)}
      </Link>
    </div>
  );
}

const RESEARCH_STATUS_LABELS: Record<SubjectResearchStatus, string> = {
  none: 'No research',
  quick_search: 'Quick search',
  research: 'Research',
};

export function PeopleControls({
  vertical,
  typed,
  statuses,
  level,
  sort,
  researchStatus,
  onResearchStatus,
  onTyped,
  onStatus,
  onSort,
}: {
  vertical: string;
  /** The live text, which is what the box shows; `q` is what it settles to. */
  typed: string;
  statuses: readonly string[];
  /**
   * Not a control any more — the address's, and `tracked` on every ordinary
   * visit. It is here for one thing: the status filter is not offered at Known.
   */
  level: SubjectListLevel;
  sort: SubjectListSort;
  researchStatus: SubjectResearchStatus | '';
  onResearchStatus: (next: SubjectResearchStatus | '') => void;
  onTyped: (next: string) => void;
  onStatus: (word: string) => void;
  onSort: (next: SubjectListSort) => void;
}) {
  const vocabulary = SUBJECT_VOCABULARY_BY_VERTICAL[vertical];

  return (
    <div className="list-tools" role="search">
      <ListSearch
        id="people-q"
        placeholder={PEOPLE_SEARCH_PLACEHOLDER}
        value={typed}
        onChange={onTyped}
      />

      {vocabulary && hasStatusChoice(vertical) && level !== 'known' ? (
        <Pick label="Status" htmlFor="people-status">
          <select
            id="people-status"
            value={statuses[0] ?? ''}
            onChange={(event) => onStatus(event.target.value)}
          >
            <option value="">All</option>
            {vocabulary.statuses.map((word) => (
              <option key={word} value={word}>
                {subjectStatusLabel(vertical, word)}
              </option>
            ))}
          </select>
        </Pick>
      ) : null}
      <Pick label="Research" htmlFor="people-research-status">
        <select
          id="people-research-status"
          value={researchStatus}
          onChange={(event) => onResearchStatus(event.target.value as SubjectResearchStatus | '')}
        >
          <option value="">All</option>
          {SUBJECT_RESEARCH_STATUSES.map((status) => (
            <option key={status} value={status}>
              {RESEARCH_STATUS_LABELS[status]}
            </option>
          ))}
        </select>
      </Pick>

      <div className="segment" role="group" aria-label="Order">
        {SUBJECT_LIST_SORTS.map((candidate) => (
          <button
            key={candidate}
            type="button"
            className={candidate === sort ? 'seg-option on' : 'seg-option'}
            aria-pressed={candidate === sort}
            onClick={() => onSort(candidate)}
          >
            {PEOPLE_SORT_LABELS[candidate]}
          </button>
        ))}
      </div>
    </div>
  );
}
